import { last } from "lodash-es";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { captureMessage } from "@/error/sentry";
import type { AlmType, Id } from "@/model/baseTypes";
import type { Iteration, Session, SessionAlmStatus } from "@/model/session";
import { goToLogout } from "@/router/navigation";
import { dayDiff, isAfter, isBefore, weekDaysBetween } from "@/utils/date";
import { asReadonly } from "@/utils/vue";

type UserSync = "unsyncedSession" | "unsynced" | "synced";

interface IterationProgress {
  daysInIteration: number;
  daysPassedInIteration: number;
  iterationsPassed: number;
}

const EMPTY_ITERATION = { id: 0, name: "", start: new Date(0), end: new Date(0) };

export const EMPTY_SESSION: Session = {
  id: "",
  name: "",
  creationDate: new Date(0),
  planningStart: null,
  planningEnd: null,
  archived: false,
  iterations: [EMPTY_ITERATION, EMPTY_ITERATION],
  almStatus: "running",
};

export const useSessionStore = defineStore(
  "session",
  () => {
    const isSessionLoading = ref(false);
    const sessions = ref<Session[]>([]);
    const current = ref(EMPTY_SESSION);
    const userSync = ref<UserSync>("unsyncedSession");
    const manualUserMapping = ref(false);
    const almType = ref<AlmType>("");

    const fullDaysSinceStart = computed(() => {
      const iterations = current.value.iterations;
      return iterations?.length ? Math.floor(dayDiff(iterations[0].start, new Date())) : undefined;
    });

    function setSessions(sess: Session[]) {
      // if we are in a session, try to find it in the list
      // set current/selected if session is found or logout if not
      sessions.value = sess;
      if (current.value.id) {
        const currentSession = sess.find((session) => session.id === current.value.id);
        if (!currentSession) {
          void goToLogout("no-session");
        } else {
          current.value = currentSession;
        }
      }
    }

    function setSession({ id: sessionId }: Id) {
      current.value = sessions.value.find((session) => session.id === sessionId) || EMPTY_SESSION;
      if (current.value.id === EMPTY_SESSION.id) {
        const sessionIds = sessions.value.map(({ id }) => id);
        captureMessage("Could not find the Session ID in the list of active sessions", {
          info: { sessionId, availableSessions: sessionIds },
        });
      }
    }

    function uniqueSessionName(session?: Session) {
      if (!session) {
        session = current.value;
      }
      let index = 1;
      for (const ss of sessions.value) {
        if (ss.id === session.id) {
          break;
        }
        if (ss.name === session.name) {
          index++;
        }
      }
      return (index > 1 ? `_${index}_` : "") + session.name;
    }

    function currentlyDifferentSession(sessionName: string) {
      return (
        !current.value || (sessionName !== uniqueSessionName() && sessionName !== current.value.id)
      );
    }

    function findByNameOrId(query: string) {
      return sessions.value.find((session) => session.name === query || session.id === query);
    }

    function findByIndexedName(sessionName: string) {
      const indexed = /_(\d+)_(.+)/.exec(sessionName || "");
      if (indexed) {
        return sessions.value.filter((session) => session.name === indexed[2])[+indexed[1] - 1];
      }
    }

    function filterArchived(archived: boolean) {
      return archived ? sessions.value : sessions.value.filter((session) => !session.archived);
    }

    function iterationProgress(date: Date, iteration?: Iteration): IterationProgress {
      const iters = current.value.iterations;
      if (isBefore(date, iters[0].start)) {
        return { daysInIteration: 0, daysPassedInIteration: 0, iterationsPassed: 0 };
      }
      if (!isBefore(date, iters[iters.length - 1].end)) {
        return { daysInIteration: 0, daysPassedInIteration: 0, iterationsPassed: iters.length };
      }
      let i = iters.length - 1;
      while (i > 0 && isAfter(iters[i].start, date)) {
        i--;
      }

      if (!iteration) {
        iteration = iters[i];
      } else {
        if (iteration.id !== iters[i].id) {
          date = isBefore(date, iteration.start) ? iteration.start : iteration.end;
        }
        i = 0;
      }

      const daysInIteration = weekDaysBetween(iteration.start, iteration.end);
      const daysPassedInIteration = Math.min(
        daysInIteration,
        weekDaysBetween(iteration.start, date),
      );

      return {
        daysInIteration,
        daysPassedInIteration,
        iterationsPassed: i + daysPassedInIteration / daysInIteration,
      };
    }

    function getIterationName(id?: number | null): string {
      if (id === null || id === undefined) return "";
      return current.value.iterations.find((i) => i.id === id)?.name || "";
    }

    function getIterationById(id?: number | null): Iteration | undefined {
      if (id === null || id === undefined) return undefined;
      return current.value.iterations.find((i) => i.id === id) || undefined;
    }

    return {
      isSessionLoading: asReadonly(isSessionLoading),
      sessions: asReadonly(sessions),
      current: asReadonly(current),
      userSync: asReadonly(userSync),
      manualUserMapping: asReadonly(manualUserMapping),
      almType: asReadonly(almType),

      fullDaysSinceStart,
      sessionHasStarted: computed(() => (fullDaysSinceStart.value ?? -1) >= 0),
      iterations: computed(() => current.value.iterations || []),
      currentIteration: computed(() => findCurrentIteration(current.value.iterations, true)),
      nearestIteration: computed(() => findCurrentIteration(current.value.iterations, false)),

      setLoading: (loading: boolean) => (isSessionLoading.value = loading),
      setSessions,
      setSession,
      setCurrent: (session: Session) => (current.value = session),
      setSessionAlmStatus: (status: SessionAlmStatus) => (current.value.almStatus = status),
      setAlmType: (type: AlmType) => (almType.value = type),
      setUserSync: (sync: UserSync) => (userSync.value = sync),
      setManualUserMapping: (manual: boolean) => (manualUserMapping.value = manual),

      uniqueSessionName,
      currentlyDifferentSession,

      findByNameOrId,
      findByIndexedName,
      filterArchived,

      iterationProgress,
      getIterationById,
      getIterationName,
    };
  },
  { autoReset: true },
);

function findCurrentIteration(iterations: readonly Iteration[], strict: boolean) {
  if (isBefore(new Date(), iterations[0].start)) {
    return strict ? undefined : iterations[0];
  }
  if (isAfter(new Date(), iterations[iterations.length - 1].end)) {
    return strict ? undefined : last(iterations);
  }
  // iteration dates might overlap, in this case, return the latest
  return iterations.findLast(
    (iter) => isAfter(new Date(), iter.start) && isBefore(new Date(), iter.end),
  )!;
}
